/* eslint import/prefer-default-export: 0 */
import { createSelector } from 'reselect';
import { SF852013, SF85 } from 'constants/formTypes';
import {
  checkValue,
  nameIsEmpty,
  dateIsEmpty,
  locationIsEmpty,
  locationCityStateIsEmpty,
  locationCityCountryIsEmpty,
  dateNoDayIsEmpty,
  valueIsEmpty,
} from 'models/validate';
import { CitizenshipStatusModel } from 'forms/sections/citizenship/status/types';
import { ValidateJSOptions } from 'forms/shared/types';
import { hasValidUSPassport as hasValidUSPassportFn } from '../validators/passport';
import { countryString } from '../validators/location';

export const certificateOfNaturalizationIsEmpty = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
) => {
  const {
    CertificateNumber,
    CertificateIssued,
    CertificateName,
    CertificateCourtAddress,
    CertificateCourtName,
    Basis,
  } = attributes;

  return (
    nameIsEmpty(CertificateName)
    && valueIsEmpty(CertificateCourtName)
    && dateIsEmpty(CertificateIssued)
    && valueIsEmpty(CertificateNumber)
    && locationIsEmpty(CertificateCourtAddress)
    && valueIsEmpty(Basis)
  );
};

export const certificateIsEmpty = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
  options: ValidateJSOptions
) => {
  const {
    CertificateNumber,
    CertificateIssued,
    CertificateName,
    CertificateCourtAddress,
    CertificateCourtName,
  } = attributes;

  // SF85 v2013 refers to Certificate of Naturalization section
  if (options.requireCitizenshipStatusCitizenshipCertificateFields) {
    return (
      valueIsEmpty(CertificateCourtName)
      && dateIsEmpty(CertificateIssued)
      && valueIsEmpty(CertificateNumber)
      && locationIsEmpty(CertificateCourtAddress)
    );
  }

  return (
    nameIsEmpty(CertificateName)
    && dateIsEmpty(CertificateIssued)
    && valueIsEmpty(CertificateNumber)
  );
};

export const documentationIsEmpty = (
  attributes: DeepSafePartial<CitizenshipStatusModel>,
  options: ValidateJSOptions
) => {
  const {
    AbroadDocumentation,
    DocumentNumber,
    DocumentIssued,
    PlaceIssued,
    DocumentName,
  } = attributes;

  // SF85 v2013 refers to Citizenship Certificate section
  if (options.requireCitizenshipStatusCitizenshipCertificateFields) {
    return (
      valueIsEmpty(DocumentNumber) && dateIsEmpty(DocumentIssued) && locationIsEmpty(PlaceIssued)
    );
  }

  return valueIsEmpty(AbroadDocumentation)
    && valueIsEmpty(DocumentNumber)
    && dateIsEmpty(DocumentIssued)
    && nameIsEmpty(DocumentName)
    && (countryString(PlaceIssued?.country) === 'United States'
      ? locationCityStateIsEmpty(PlaceIssued)
      : locationCityCountryIsEmpty(PlaceIssued, true));
};

export const form240IsEmpty = (attributes: DeepSafePartial<CitizenshipStatusModel>) => {
  const { Form240Date, Form240DateExplanation } = attributes;

  return valueIsEmpty(Form240DateExplanation) && dateNoDayIsEmpty(Form240Date);
};

export const selectValidUSPassport = createSelector(
  (state) => {
    const { application = {} } = state;
    const { Foreign = {} } = application;
    return hasValidUSPassportFn(Foreign.Passport);
  },
  (hasValidUSPassport) => ({
    hasValidUSPassport,
  })
);

export const selectRequiredDocumentsSF852013 = createSelector(
  (state) => {
    const { Foreign = {}, Settings = {}, Citizenship = {} } = state?.application || {};
    if (
      Foreign.Passport && Citizenship.Status?.CitizenshipStatus?.value === 'Naturalized'
      && (Settings.formType === SF85 || Settings.formType === SF852013)
      && Settings.formVersion === '2013-12'
    ) {
      return (
        documentationIsEmpty(Citizenship.Status, { requireCitizenshipStatusCitizenshipCertificateFields: true })
        && certificateIsEmpty(Citizenship.Status, { requireCitizenshipStatusCitizenshipCertificateFields: true })
        && form240IsEmpty(Citizenship.Status)
        && !checkValue(Foreign.Passport?.HasPassports, 'Yes')
      );
    }
    return false;
  },
  (requiredDocumentsEmptySF852013) => ({
    requiredDocumentsEmptySF852013,
  })
);
